import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import Badge from '@pelckmans/elementis/components/badge';
import Navigation from '@pelckmans/elementis/components/navigation';
import LessonsIcon from '@pelckmans/elementis/icons/lessons';
import PelckmansIcon from '@pelckmans/elementis/icons/pelckmans';
import ResultsIcon from '@pelckmans/elementis/icons/results';
import SearchIcon from '@pelckmans/elementis/icons/search';
import TaskIcon from '@pelckmans/elementis/icons/tasks';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountContext from '../../contexts/AccountContext';
import IdentityContext from '../../contexts/IdentityContext';
import MyAssignmentsContext from '../../contexts/MyAssignmentsContext';
import { useRoutes } from '../../hooks/useRoutes';
import ModuleContext from '../../pages/material/_contexts/ModuleContext';
import UserMenu from './UserMenu';

type Props = {
  includeSideNavigation?: boolean;
};

export default function PageNavigation({ includeSideNavigation = true }: Props) {
  const { identity } = useContext(IdentityContext);
  const { isTeacherAlike } = identity;
  const { getRoute, buildAbsolutePath } = useRoutes();
  const { t } = useTranslation();
  const {
    account: { avatar },
  } = useContext(AccountContext);
  const { module: superModule } = useContext(ModuleContext);
  const { assignments } = useContext(MyAssignmentsContext);

  const params = {
    subjectSlug: superModule?.subjectSlug || '',
    methodSlug: superModule?.methodSlug || '',
    moduleSlug: superModule?.slug || '',
  };

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [refEl, setRefEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const menuElement = document.getElementById('user-menu');
      if (menuElement && !menuElement.contains(event.target as Node) && !refEl?.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [refEl, showUserMenu]);

  return (
    <aside className="page-navigation">
      <div className="top-navigation">
        <Navigation aria-label={t('APP_NAV.SIDE_NAV.HOME_NAVIGATION.A11Y_LABEL')}>
          <Navigation.List>
            <Navigation.Item
              icon={<PelckmansIcon data-testid="pelckmans-icon" />}
              iconOnly
              to={`/ianua/${getRoute('MY_METHODS')}`}
            >
              {t('APP_NAV.SIDE_NAV.HOME')}
            </Navigation.Item>
          </Navigation.List>
        </Navigation>
      </div>
      {includeSideNavigation && superModule && (
        <div className="side-navigation">
          <Navigation aria-label={t('APP_NAV.SIDE_NAV.MATERIAL_NAVIGATION.A11Y_LABEL')}>
            <Navigation.List orientation="vertical">
              <Navigation.Item icon={<LessonsIcon />} iconOnly to={buildAbsolutePath(['MATERIAL'], params)}>
                {t('APP_NAV.SIDE_NAV.MODULES')}
              </Navigation.Item>
              <Navigation.Item
                badge={assignments.length ? <Badge>{assignments.length}</Badge> : undefined}
                icon={<TaskIcon />}
                iconOnly
                to={buildAbsolutePath(['MATERIAL', 'MATERIAL.ASSIGNMENTS'], params)}
              >
                {t('APP_NAV.SIDE_NAV.ASSIGNMENTS')}
              </Navigation.Item>
              {isTeacherAlike && (
                <Navigation.Item
                  icon={<ResultsIcon />}
                  iconOnly
                  to={buildAbsolutePath(['MATERIAL', 'MATERIAL.RESULTS'], params)}
                >
                  {t('APP_NAV.SIDE_NAV.RESULTS')}
                </Navigation.Item>
              )}
              <Navigation.Item
                icon={<SearchIcon />}
                iconOnly
                to={buildAbsolutePath(['MATERIAL', 'MATERIAL.SEARCH'], params)}
              >
                {t('APP_NAV.SIDE_NAV.SEARCH')}
              </Navigation.Item>
            </Navigation.List>
          </Navigation>
        </div>
      )}
      <div className="bottom-navigation" data-testid="bottom-navigation" ref={ref => setRefEl(ref)}>
        <Avatar
          onClick={() => {
            setShowUserMenu(x => !x);
          }}
        >
          <AvatarImage name={t('APP_NAV.SIDE_NAV.IMAGE.ALT')} src={avatar} size="lg" />
        </Avatar>
      </div>
      {showUserMenu && <UserMenu referenceEl={refEl} />}
    </aside>
  );
}
